import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, Button, TextInput } from '@op/opds.opux';
import { space } from '@op/opds.tokens';
import { useTranslation } from 'react-i18next';
import { AutoSuggestOption } from '../utils/types';

interface SearchBoxProps {
  query: string;
  setQuery: (query: string) => void;
  fetchOfficeResults: (query: string) => void;
  autoSuggestOptions?: AutoSuggestOption[];
}

export const SearchBox: React.FC<SearchBoxProps> = ({
  query,
  setQuery,
  fetchOfficeResults,
  autoSuggestOptions = []
}) => {
  const { t } = useTranslation();
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const [displayValue, setDisplayValue] = useState<string>(query);
  const typingRef = useRef(false);
  const cyclingRef = useRef(false);

  // Only sync displayValue to query when typing, not when cycling
  useEffect(() => {
    if (cyclingRef.current) {
      cyclingRef.current = false;
      return;
    }
    setSelectedIndex(-1);
    setDisplayValue(query);
    typingRef.current = false;
  }, [query]);

  useEffect(() => {
    setSelectedIndex(-1);
    setDisplayValue(query);
  }, [autoSuggestOptions]);

  const handleSearch = () => {
    if (displayValue.length >= 3) {
      if (autoSuggestOptions.length > 0) {
        // If user has cycled with down key, use selectedIndex
        const indexToUse = selectedIndex >= 0 ? selectedIndex : 0;
        setQuery(autoSuggestOptions[indexToUse].term);
        fetchOfficeResults(autoSuggestOptions[indexToUse].term);
        setDisplayValue(autoSuggestOptions[indexToUse].term);
      } else {
        setQuery(displayValue);
        fetchOfficeResults(displayValue);
      }
    }
  };

  return (
    <Row
      style={{
        marginLeft: '-1rem',
        marginTop: 0,
        paddingTop: 0,
        alignContent: 'flex-start'
      }}
      start
    >
      <Col xs={9} sm={9} lg={9}>
        <TextInput
          value={displayValue}
          required={false}
          className="search-input"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            typingRef.current = true;
            setDisplayValue((e.target as HTMLInputElement).value);
            setQuery((e.target as HTMLInputElement).value);
          }}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
              // On Enter, update parent query and trigger search
              if (selectedIndex >= 0 && autoSuggestOptions[selectedIndex]) {
                setQuery(autoSuggestOptions[selectedIndex].term);
                setDisplayValue(autoSuggestOptions[selectedIndex].term);
              } else {
                setQuery(displayValue);
              }
              handleSearch();
            } else if (
              e.key === 'ArrowDown' &&
              displayValue.length >= 3 &&
              autoSuggestOptions.length > 0
            ) {
              e.preventDefault();
              cyclingRef.current = true;
              setSelectedIndex((prevIndex) => {
                const nextIndex = prevIndex + 1 < autoSuggestOptions.length ? prevIndex + 1 : 0;
                setDisplayValue(autoSuggestOptions[nextIndex].term);
                return nextIndex;
              });
            } else if (
              e.key === 'ArrowUp' &&
              displayValue.length >= 3 &&
              autoSuggestOptions.length > 0
            ) {
              e.preventDefault();
              cyclingRef.current = true;
              setSelectedIndex((prevIndex) => {
                const nextIndex = prevIndex > 0 ? prevIndex - 1 : autoSuggestOptions.length - 1;
                setDisplayValue(autoSuggestOptions[nextIndex].term);
                return nextIndex;
              });
            }
          }}
          // Use selectedIndex to avoid eslint warning (for accessibility, highlight option)
          aria-activedescendant={
            selectedIndex >= 0 && selectedIndex < autoSuggestOptions.length
              ? `autosuggest-option-${selectedIndex}`
              : undefined
          }
        />
      </Col>
      <Col xs={3} sm={3} lg={3}>
        <Button
          data-testid="submit"
          primary
          size="default"
          onClick={handleSearch}
          style={{
            borderRadius: '3px',
            lineHeight: space.small
          }}
          onFocus={(e: React.FocusEvent<HTMLElement>) => {
            e.currentTarget.style.outline = '2px solid #0066cc';
            e.currentTarget.style.outlineOffset = '2px';
            e.currentTarget.style.boxShadow = '0 0 0 2px #0066cc';
          }}
          onBlur={(e: React.FocusEvent<HTMLElement>) => {
            e.currentTarget.style.outline = '';
            e.currentTarget.style.outlineOffset = '';
            e.currentTarget.style.boxShadow = '';
          }}
        >
          {t('search.button')}
        </Button>
      </Col>
    </Row>
  );
};